/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './accueil.css';
import HeaderFirst from 'utils/HeaderFirst';
import PeriodFilter from 'components/PeriodFilter/PeriodFilter';
/*import bleu from './../../image/jaune.png';
import Rectangle from '../../image/bleu.png';
import Rectangl from '../../image/rouge.png';
import Rect from '../../image/rose.png';
import vertical from '../../image/layout.png';
import user from '../../image/user.png';
import credit from '../../image/credit.png';
import change from '../../image/change.png';*/
import { useNavigate, useParams } from 'react-router-dom';
import { CREANCES, DETTE_PATH, TRANSACTION } from 'routes/navigation/navigationPaths';
import Cover from 'utils/cover';
//import GridList from 'react-flexible-list';
//import SkeletonComponent from 'utils/skeleton/skeleton';
import { useGetTransactionQuery, useGetDossierIdQuery } from 'actions/dossiers/queries';
// import { useGetCompteResultatQuery, useGetBalanceQuery } from 'actions/comptabilite/queries';
//import { useGetOcrQuery } from 'actions/orc/queries';
import { useGetAllCreancesQuery } from 'actions/creances/queries';
import { useGetAllDettesQuery } from 'actions/dettes/queries';
import { useSelector } from 'react-redux';
import { getUser } from 'redux/features/userSliceWithTokenValidation';
import { FaBuilding } from 'react-icons/fa';
import TableLoader from 'components/Loader/TableLoader';

function Accueil() {
  const { clientID: dossier_id } = useParams();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [periodFilter, setPeriodFilter] = useState({ type: 'all', startDate: null, endDate: null });

  // Récupérer les informations du dossier (entreprise)
  const userStatus = useSelector(getUser);
  const comptableId = userStatus?.id;
  const { data: dossierData } = useGetDossierIdQuery({ comptableId, id: dossier_id });
  const entreprise = dossierData?.data?.find(d => d.id === parseInt(dossier_id, 10));

  const { data: creancesResponse, isLoading: isCreancesLoading } = useGetAllCreancesQuery(
    { dossier_id }
  );
  const { data: dettesResponse, isLoading: isDettesLoading } = useGetAllDettesQuery(
    { dossier_id }
  );
  const { data: getAllTransactions, isLoading: isTransLoading } = useGetTransactionQuery(
    { dossier_id }
  );

  /* EC-31 : Données comptables pour le dashboard dirigeant (commenté temporairement)
  // EC-31 : Données comptables pour le dashboard dirigeant (commenté temporairement)
  const { data: compteResultatData, refetch: refetchCompteResultat } = useGetCompteResultatQuery(
    { dossier_id },
    { skip: !dossier_id }
  );
  const { data: balanceData, refetch: refetchBalance } = useGetBalanceQuery(
    { dossier_id },
    { skip: !dossier_id }
  );

  useEffect(() => {
    const handleComptaRefresh = () => {
      if (refetchCompteResultat) refetchCompteResultat();
      if (refetchBalance) refetchBalance();
    };
    window.addEventListener("compta:refresh", handleComptaRefresh);
    return () => window.removeEventListener("compta:refresh", handleComptaRefresh);
  }, [refetchCompteResultat, refetchBalance]);
  */

  const isDashboardLoading = (isCreancesLoading || isDettesLoading || isTransLoading) && (!creancesResponse && !dettesResponse && !getAllTransactions);
  //const { data: getDataFacture } = useGetOcrQuery();

  // ── KPIs ─────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    // Fonction de filtrage par date (définie dans useMemo pour accéder à periodFilter)
    const filterByDate = (items, dateField) => {
      if (!items || !Array.isArray(items)) return [];
      
      if (periodFilter.type === 'all' || !periodFilter.startDate || !periodFilter.endDate) {
        return items;
      }

      const startDate = new Date(periodFilter.startDate);
      const endDate = new Date(periodFilter.endDate);
      endDate.setHours(23, 59, 59, 999); // Inclure toute la journée de fin

      return items.filter(item => {
        if (!item[dateField]) return false;
        const itemDate = new Date(item[dateField]);
        return itemDate >= startDate && itemDate <= endDate;
      });
    };

    const allCreances     = creancesResponse?.data    || [];
    const allDettes       = dettesResponse?.data      || [];
    const allTransactions = getAllTransactions?.transactions || [];

    // Appliquer le filtre de période
    const creances     = filterByDate(allCreances, 'date_echeance');
    const dettes       = filterByDate(allDettes, 'date_echeance');
    const transactions = filterByDate(allTransactions, 'date');

    // Debug: afficher les résultats du filtrage
    if (periodFilter.type !== 'all') {
      console.log('🔍 Filtre de période actif:', periodFilter);
      console.log('📊 Créances:', allCreances.length, '→', creances.length);
      console.log('📊 Dettes:', allDettes.length, '→', dettes.length);
      console.log('📊 Transactions:', allTransactions.length, '→', transactions.length);
    }

    const totalCreances = creances.reduce((s, i) => s + (i.reste_a_payer || 0), 0);
    const totalDettes   = dettes.reduce((s, i) => s + (i.reste_a_payer || 0), 0);
    const soldeNet      = totalCreances - totalDettes;

    const nbTransactions = transactions.filter(i => i.archived === false).length;
    const totalPaye      = transactions.reduce((s, i) => s + (i.montant_paye || 0), 0);

    const totalCreancesMontant = creances.reduce((s, i) => s + (i.montant_total || 0), 0);
    const tauxRecouvrement     = totalCreancesMontant > 0
      ? ((totalCreancesMontant - totalCreances) / totalCreancesMontant * 100).toFixed(1) : 0;

    const totalDettesMontant = dettes.reduce((s, i) => s + (i.montant_total || 0), 0);
    const tauxPaiementDettes = totalDettesMontant > 0
      ? ((totalDettesMontant - totalDettes) / totalDettesMontant * 100).toFixed(1) : 0;

    // ── Trésorerie disponible - depuis transactions_par_compte ─────────
    // Structure : { Banque: {solde: 60000}, Caisse: {solde: 30000}, ... }
    const comptesMap = getAllTransactions?.transactions_par_compte || {};
    const totalDisponible = Object.values(comptesMap).reduce(
      (s, c) => s + (c.solde || 0), 0
    );

    // ── Produits vendus / achetés - depuis produits_associes ──────────
    // Chaque transaction a un champ "type" ('entree' | 'sortie') et
    // "produits_associes" ([{quantite, ...}])
    let totalProduitsVendus  = 0;
    let totalProduitsAchetes = 0;

    transactions.forEach(t => {
      const qte = (t.produits_associes || []).reduce(
        (s, p) => s + (Number(p.quantite) || 0), 0
      );
      if (t.type === 'entree') totalProduitsVendus  += qte;
      if (t.type === 'sortie') totalProduitsAchetes += qte;
    });

    return {
      totalCreances, totalDettes, soldeNet,
      nbTransactions, totalPaye,
      tauxRecouvrement, tauxPaiementDettes,
      nbCreances: creances.length,
      nbDettes:   dettes.length,
      totalDisponible,         // ← NOUVEAU
      totalProduitsVendus,     // ← NOUVEAU
      totalProduitsAchetes,    // ← NOUVEAU
      comptesMap,              // ← pour la section soldes par compte
    };
  }, [creancesResponse, dettesResponse, getAllTransactions, periodFilter]);

  // ── Graphiques ───────────────────────────────────────────────────────
  const barChartData = [
    { name: 'Créances', 'Montant Total': kpis.totalCreances, fill: '#3B82F6' },
    { name: 'Dettes',   'Montant Total': kpis.totalDettes,   fill: '#EF4444' },
  ];

  /* EC-31 : KPIs comptables (compte de résultat + balance par classe) (commenté temporairement)
  // EC-31 : KPIs comptables (compte de résultat + balance par classe)
  const kpisComptables = useMemo(() => {
    const cr = compteResultatData?.data || compteResultatData || {};
    const totalProduits = Number(cr.produits?.total || cr.total_produits || 0);
    const totalCharges = Number(cr.charges?.total || cr.total_charges || 0);
    const resultat = totalProduits - totalCharges;

    // Soldes par classe depuis la balance
    const balance = balanceData?.data || balanceData || [];
    const soldesParClasse = {};
    (Array.isArray(balance) ? balance : []).forEach((ligne) => {
      const code = String(ligne.code || ligne.compte || "");
      if (code.length > 0) {
        const classe = code[0];
        const solde = Number(ligne.solde || ligne.cumul_solde || 0);
        if (!soldesParClasse[classe]) soldesParClasse[classe] = 0;
        soldesParClasse[classe] += solde;
      }
    });

    return {
      totalProduits,
      totalCharges,
      resultat,
      soldesParClasse,
    };
  }, [compteResultatData, balanceData]);
  */
  const pieChartData = [
    { name: 'Payé',                     value: kpis.totalPaye,     color: '#10B981' },
    { name: 'Reste à payer (Créances)', value: kpis.totalCreances, color: '#3B82F6' },
    { name: 'Reste à payer (Dettes)',   value: kpis.totalDettes,   color: '#EF4444' },
  ];
  const progressData = [
    { name: 'Recouvrement Créances', valeur: parseFloat(kpis.tauxRecouvrement) },
    { name: 'Paiement Dettes',       valeur: parseFloat(kpis.tauxPaiementDettes) },
  ];

  // ── Détails créances/dettes par compte ───────────────────────────────
  const compteDetails = useMemo(() => {
    // Fonction de filtrage par date
    const filterByDate = (items, dateField) => {
      if (!items || !Array.isArray(items)) return [];
      
      if (periodFilter.type === 'all' || !periodFilter.startDate || !periodFilter.endDate) {
        return items;
      }

      const startDate = new Date(periodFilter.startDate);
      const endDate = new Date(periodFilter.endDate);
      endDate.setHours(23, 59, 59, 999);

      return items.filter(item => {
        if (!item[dateField]) return false;
        const itemDate = new Date(item[dateField]);
        return itemDate >= startDate && itemDate <= endDate;
      });
    };

    const allCreances = creancesResponse?.data || [];
    const allDettes   = dettesResponse?.data   || [];

    // Appliquer le filtre de période
    const creances = filterByDate(allCreances, 'date_echeance');
    const dettes   = filterByDate(allDettes, 'date_echeance');

    const creancesParCompte = creances.reduce((acc, item) => {
      const c = item.source_paiement || 'Non défini';
      if (!acc[c]) acc[c] = { montant: 0, nombre: 0 };
      acc[c].montant += item.reste_a_payer || 0;
      acc[c].nombre  += 1;
      return acc;
    }, {});

    const dettesParCompte = dettes.reduce((acc, item) => {
      const c = item.source_paiement || 'Non défini';
      if (!acc[c]) acc[c] = { montant: 0, nombre: 0 };
      acc[c].montant += item.reste_a_payer || 0;
      acc[c].nombre  += 1;
      return acc;
    }, {});

    return { creancesParCompte, dettesParCompte };
  }, [creancesResponse, dettesResponse, periodFilter]);

  const navigate = useNavigate();

  const formatCurrency = (v) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(v);

  // Couleur badge solde selon niveau
  const soldeBadgeColor = (solde) => {
    if (solde <= 0)      return { bg: '#FEE2E2', text: '#DC2626' };
    if (solde < 100000)  return { bg: '#FEF3C7', text: '#D97706' };
    return                      { bg: '#D1FAE5', text: '#065F46' };
  };

  return (
    <div className="global" style={{ backgroundColor: 'white', minHeight: '800px', borderRadius: '20px', padding: '15px', width: '100%' }}>
      <Cover />
      <br />
      <div className="header" style={{ backgroundColor: 'white', borderRadius: '10px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <HeaderFirst
            nom={<div style={{ paddingTop: '40px' }}>Tableau de Bord</div>}
            description={
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span>Voici un aperçu global de vos différentes données</span>
                {periodFilter.type !== 'all' && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#3B82F6',
                    backgroundColor: '#EFF6FF',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    border: '1px solid #BFDBFE',
                  }}>
                    🔍 Filtre actif
                  </span>
                )}
              </div>
            }
          />
          
          {/* Badge Nom Entreprise */}
          {entreprise && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              paddingTop: '40px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                backgroundColor: '#FFFFFF',
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              }}>
                <div style={{
                  width: '35px',
                  height: '35px',
                  borderRadius: '6px',
                  backgroundColor: '#068f7ac0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                }}>
                <FaBuilding className="h-5 w-5" />
                </div>
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '2px',
                  }}>
                    ENTREPRISE
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1F2937',
                  }}>
                    {entreprise.nom_entreprise}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filtre de période - Placé après le header pour ne pas casser la responsivité */}
      <div style={{ marginTop: '20px', marginBottom: '10px' }}>
        <PeriodFilter 
          onFilterChange={setPeriodFilter}
          initialFilter={periodFilter}
        />
      </div>

      {/* ── KPIs & Dashboard Content ────────────────────────────────────────────────── */}
      {isDashboardLoading ? (
        <div style={{ background: '#ffffff', borderRadius: '16px', padding: '40px', marginTop: '20px', border: '1px solid #e2eced' }}>
          <TableLoader
            message="Chargement du tableau de bord..."
            subMessage="Calcul des indicateurs financiers, créances, dettes et trésorerie"
            minHeight="350px"
          />
        </div>
      ) : (
        <>
          <div className="mt-4">
            <h3 style={sectionTitleStyle}>Indicateurs Clés de Performance</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>

          {/* Solde Net */}
          <KpiCard
            color="#10B981" hovered={hoveredCard === 'solde'}
            onEnter={() => setHoveredCard('solde')} onLeave={() => setHoveredCard(null)}
            onClick={() => navigate(CREANCES)}
            label="Solde Net" value={formatCurrency(kpis.soldeNet)} sub="Créances − Dettes"
            tooltip="Différence entre vos créances clients et vos dettes fournisseurs"
          />

          {/* Total Créances */}
          <KpiCard
            color="#3B82F6" hovered={hoveredCard === 'creances'}
            onEnter={() => setHoveredCard('creances')} onLeave={() => setHoveredCard(null)}
            onClick={() => navigate(CREANCES)}
            label="Total Créances" value={formatCurrency(kpis.totalCreances)}
            sub={`${kpis.nbCreances} créance(s) · ${kpis.tauxRecouvrement}% recouvrées`}
            tooltip="Montant total des factures clients non encore encaissées"
          />

          {/* Total Dettes */}
          <KpiCard
            color="#EF4444" hovered={hoveredCard === 'dettes'}
            onEnter={() => setHoveredCard('dettes')} onLeave={() => setHoveredCard(null)}
            onClick={() => navigate(DETTE_PATH)}
            label="Total Dettes" value={formatCurrency(kpis.totalDettes)}
            sub={`${kpis.nbDettes} dette(s) · ${kpis.tauxPaiementDettes}% payées`}
            tooltip="Montant total des factures fournisseurs non encore réglées"
          />

          {/* Transactions */}
          <KpiCard
            color="#8B5CF6" hovered={hoveredCard === 'transactions'}
            onEnter={() => setHoveredCard('transactions')} onLeave={() => setHoveredCard(null)}
            onClick={() => navigate(TRANSACTION)}
            label="Transactions" value={kpis.nbTransactions}
            sub={`${formatCurrency(kpis.totalPaye)} payés`}
            tooltip="Nombre total de mouvements financiers enregistrés"
          />

          {/* ── NOUVEAU : Trésorerie Disponible ───────────────────────── */}
          <KpiCard
            color="#F59E0B" hovered={hoveredCard === 'disponible'}
            onEnter={() => setHoveredCard('disponible')} onLeave={() => setHoveredCard(null)}
            label="Trésorerie Disponible"
            value={formatCurrency(kpis.totalDisponible)}
            sub={`${Object.keys(kpis.comptesMap).length} compte(s) actif(s)`}
            tooltip="Somme des soldes disponibles sur tous vos comptes"
            valueColor={kpis.totalDisponible >= 0 ? '#065F46' : '#DC2626'}
            icon="💰"
          />

          {/* ── NOUVEAU : Produits Vendus ──────────────────────────────── */}
          <KpiCard
            color="#06B6D4" hovered={hoveredCard === 'vendus'}
            onEnter={() => setHoveredCard('vendus')} onLeave={() => setHoveredCard(null)}
            onClick={() => navigate(TRANSACTION)}
            label="Produits Vendus"
            value={kpis.totalProduitsVendus}
            sub="unités vendues (entrées)"
            tooltip="Nombre total d'unités vendues sur toutes les transactions client"
            icon="📦"
          />

          {/* ── NOUVEAU : Produits Achetés ─────────────────────────────── */}
          <KpiCard
            color="#F97316" hovered={hoveredCard === 'achetes'}
            onEnter={() => setHoveredCard('achetes')} onLeave={() => setHoveredCard(null)}
            onClick={() => navigate(TRANSACTION)}
            label="Produits Achetés"
            value={kpis.totalProduitsAchetes}
            sub="unités achetées (sorties)"
            tooltip="Nombre total d'unités achetées sur toutes les transactions fournisseur"
            icon="🛒"
          />

        </div>
      </div>

      {/* ── Graphiques ────────────────────────────────────────────────── */}
      <div className="mt-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
        <div style={chartContainerStyle}>
          <h4 style={chartTitleStyle}>Comparaison Créances vs Dettes</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" /><YAxis />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="Montant Total" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={chartContainerStyle}>
          <h4 style={chartTitleStyle}>Répartition des Montants</h4>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieChartData} cx="50%" cy="50%" labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80} dataKey="value">
                {pieChartData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={chartContainerStyle}>
          <h4 style={chartTitleStyle}>Taux de Performance (%)</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={progressData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis type="category" dataKey="name" width={150} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="valeur" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Détails par Compte de Paiement ────────────────────────────── */}
      <div className="mt-4">
        <h3 style={sectionTitleStyle}>Détails par Compte de Paiement</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>

          {/* ── NOUVEAU : Solde disponible par compte ─────────────────── */}
          <div style={chartContainerStyle}>
            <h4 style={chartTitleStyle}>💰 Solde Disponible par Compte</h4>

            {Object.keys(kpis.comptesMap).length === 0 ? (
              <div style={emptyStyle}>Aucun mouvement enregistré</div>
            ) : (
              <>
                {/* Total */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 14px', marginBottom: '12px',
                  backgroundColor: '#F0FDF4', borderRadius: '10px',
                  border: '1px solid #BBF7D0',
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#065F46' }}>Total disponible</div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>
                      {Object.keys(kpis.comptesMap).length} compte(s)
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#065F46' }}>
                    {formatCurrency(kpis.totalDisponible)}
                  </div>
                </div>

                {/* Une ligne par compte */}
                {Object.entries(kpis.comptesMap).map(([nomCompte, data]) => {
                  const solde  = data.solde || 0;
                  const colors = soldeBadgeColor(solde);
                  const pct    = kpis.totalDisponible > 0
                    ? Math.max(0, Math.min(100, (solde / kpis.totalDisponible) * 100)) : 0;

                  return (
                    <div key={nomCompte} style={compteItemStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, textTransform: 'capitalize' }}>
                          {nomCompte}
                        </div>
                        <div style={{
                          backgroundColor: colors.bg, color: colors.text,
                          fontWeight: 700, fontSize: 14,
                          padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap',
                        }}>
                          {formatCurrency(solde)}
                        </div>
                      </div>
                      {/* Barre de proportion */}
                      <div style={{ height: 5, borderRadius: 4, backgroundColor: '#E5E7EB', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', width: `${pct}%`, borderRadius: 4,
                          backgroundColor: colors.text, transition: 'width 0.6s ease',
                        }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 3, textAlign: 'right' }}>
                        {pct.toFixed(0)}% du total
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Créances par compte */}
          <div style={chartContainerStyle}>
            <h4 style={chartTitleStyle}>Créances par Compte</h4>
            <div style={{ padding: '10px' }}>
              {Object.entries(compteDetails.creancesParCompte).map(([compte, data]) => (
                <div key={compte} style={compteItemStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{compte}</div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>{data.nombre} créance(s)</div>
                    </div>
                    <div style={{ fontWeight: '700', fontSize: '16px', color: '#3B82F6' }}>
                      {formatCurrency(data.montant)}
                    </div>
                  </div>
                </div>
              ))}
              {Object.keys(compteDetails.creancesParCompte).length === 0 && (
                <div style={emptyStyle}>Aucune créance</div>
              )}
            </div>
          </div>

          {/* Dettes par compte */}
          <div style={chartContainerStyle}>
            <h4 style={chartTitleStyle}>Dettes par Compte</h4>
            <div style={{ padding: '10px' }}>
              {Object.entries(compteDetails.dettesParCompte).map(([compte, data]) => (
                <div key={compte} style={compteItemStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{compte}</div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>{data.nombre} dette(s)</div>
                    </div>
                    <div style={{ fontWeight: '700', fontSize: '16px', color: '#EF4444' }}>
                      {formatCurrency(data.montant)}
                    </div>
                  </div>
                </div>
              ))}
              {Object.keys(compteDetails.dettesParCompte).length === 0 && (
                <div style={emptyStyle}>Aucune dette</div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* EC-31 : Situation Comptable (Dashboard Dirigeant) - Désactivé pour la recette */}
      </>
      )}

      {/* ── Accès Rapide ──────────────────────────────────────────────── 
      <div className="mt-4">
        <h3 style={sectionTitleStyle}>Accès Rapide</h3>
        {isLoading ? (
          <GridList cardWidth={200} resourceData={[1,2,3,4]} resourceItem={() => <SkeletonComponent />} />
        ) : (
          <GridList
            cardWidth={230}
            resourceData={[
              { id:1, title:'Dettes Fournisseurs', image:bleu,      image2:vertical, count:dettesResponse?.data?.length,                                                    onClick:DETTE_PATH  },
              { id:2, title:'Créances Clients',    image:Rectangle,  image2:user,     count:creancesResponse?.data?.length,                                                   onClick:CREANCES    },
              { id:3, title:'Factures',            image:Rectangl,   image2:credit,   count:getDataFacture?.filter(i => i.archived == false).count,                           onClick:FACTURE     },
              { id:4, title:'Transactions',        image:Rect,        image2:change,   count:getAllTransactions?.transactions?.filter(i => i.archived == false).length || 0,          onClick:TRANSACTION },
            ]}
            resourceItem={(item) => (
              <div className="cartes" style={cardStyle} onClick={() => navigate(item.onClick)}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'15px' }}>
                  <div style={{ display:'flex' }}>
                    <div><img src={item.image} alt={item.title} style={{ marginRight:'10px' }} /></div>
                    <div>
                      <p style={{ fontWeight:'700', fontSize:'27px' }}>{item.count}</p>
                      <h2 style={{ fontSize:'12px', fontWeight:'700' }}>{item.title}</h2>
                    </div>
                  </div>
                  <div><img src={item.image2} alt="" /></div>
                </div>
              </div>
            )}
          />
        )}
      </div> */}
    </div>
  );
}

// ── Composant KpiCard ─────────────────────────────────────────────────
function KpiCard({ color, hovered, onEnter, onLeave, onClick, label, value, sub, tooltip, valueColor, icon }) {
  return (
    <div
      style={{
        backgroundColor: '#FFF',
        padding: '20px',
        borderRadius: '15px',
        boxShadow: hovered ? '0 8px 16px rgba(0,0,0,0.25)' : '0 4px 8px rgba(0,0,0,0.1)',
        borderLeft: `4px solid ${color}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        position: 'relative',
        zIndex: hovered ? 2 : 1,
      }}
      onClick={onClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '14px', color: '#6B7280' }}>{label}</div>
        {icon && (
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            backgroundColor: `${color}22`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
          }}>{icon}</div>
        )}
      </div>
      <div style={{ fontSize: '26px', fontWeight: '700', marginTop: '8px', color: valueColor || '#111827' }}>
        {value}
      </div>
      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '5px' }}>{sub}</div>
      {hovered && tooltip && (
        <div style={tooltipStyle}>{tooltip}</div>
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────
const sectionTitleStyle = { fontSize: '18px', fontWeight: '700', marginBottom: '15px' };

const chartContainerStyle = {
  backgroundColor: '#FFF', padding: '20px',
  borderRadius: '15px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
};

const chartTitleStyle = {
  fontSize: '16px', fontWeight: '700', marginBottom: '15px', color: '#1F2937',
};

const compteItemStyle = {
  padding: '12px', borderBottom: '1px solid #E5E7EB', marginBottom: '8px',
};

const emptyStyle = { textAlign: 'center', color: '#9CA3AF', padding: '20px' };

/*const cardStyle = {
  backgroundColor: '#FFF', padding: '10px', borderRadius: '20px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.25)', minWidth: '200px',
  height: '120px', marginBottom: '15px', cursor: 'pointer', transition: 'transform 0.2s',
};*/

const tooltipStyle = {
  position: 'absolute', bottom: '-50px', left: '50%',
  transform: 'translateX(-50%)', backgroundColor: '#1F2937',
  color: '#FFF', padding: '8px 12px', borderRadius: '8px',
  fontSize: '12px', whiteSpace: 'nowrap', zIndex: 10,
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
};

export default Accueil;